import * as React from 'react';
import {
  Stack,
  MessageBarType,
  MessageBarButton,
  IStyleFunctionOrObject,
  IStyle,
  concatStyleSets
} from 'office-ui-fabric-react';
import { ICriticalContainerError, ContainerErrorType, ContainerWarning } from '@fluidframework/container-definitions';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OdspErrorType } from '@fluidframework/odsp-doclib-utils';
import {
  ExternalContainer,
  ITelemetryNamespaceLogger,
  OWHErrorType,
  OfficeFluidErrorType
} from '@fluidx/office-fluid-types';
import { useFFXHostUxIcon } from '@fluidx/icons';
import {
  isMobile,
  Locale,
  LocaleDirection,
  NamespaceLoggerAdapter,
  sendTelemetryEvent,
  UserActivityTracker,
  useStringTable
} from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { ContainerConnectionStateTracker } from './ContainerConnectionStateTracker';
import { MessageBanner, MessageBannerStrings, MessageBannerStyleProps, MessageBannerStyles } from './MessageBanner';
import {
  BannerErrorInfo,
  getBannerMessagingForError,
  getBannerMessagingForContainerState
} from './getBannerMessagingForError';
import { GenericEvent, GenericEventSubcategories } from '../../../telemetry/GenericEvent';

/**
 * @deprecated
 */
export interface ContainerMessageBannerProps {
  /** Loaded container */
  container: ExternalContainer;

  /**
   * The action handler callback for different error scenarios.
   * It's implementation should fan out to handle all expected action types below.
   *
   * Reload - Reloads container and component.   *
   * Upgrade - Upgrade version of container and component. Usually this requires a Reload   *
   * ViewFileLocation - Open file storage location. User has been prompted to delete some files in ODSP out of storage situations.
   */
  messageBannerActionHandlers: <
    ActonType extends 'Reload' | 'Upgrade' | 'ViewFileLocation',
    ErrorType extends OfficeFluidErrorType
  >(
    actionType: ActonType,
    errorType: ErrorType
  ) => void;

  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };

  logger?: ITelemetryNamespaceLogger;

  /**
   * Custom styling for the message bar.
   * To customize background of the rendered message bars based on the messageBarType, specify messageBarType and the desired background color.
   *
   *  Example:
   *
   *    <ContainerMessageBanner styles={{ background: { [MessageBarType.error]: '#8B0000', [MessageBarType.info]: '#008B8B' } }} />
   *
   *  Renders:
   *
   *    <MessageBar messageBarType={MessageBarType.error} styles={{ root.background: '#8B0000' }} /> and
   *    <MessageBar messageBarType={MessageBarType.info} styles={{ root.background: '#008B8B' }} /> respectively.
   */
  styles?: ContainerMessageBarStyles;
}

/**
 * A UX control to handle PostLoad fluid errors using Banner messaging
 * PostLoad errors handled originate from Fluid Container
 * and can be of ContainerErrorType or DriverErrorType.
 *
 * Where this control is too opinionated on things like recovery actions possible for each error handled and easy configuration is not possible,
 * It's recommended a host builds their own equivalent error handling control using with the underlying error handling utilities
 *
 * @deprecated Replace with:
 *  1. MessageBar - Loads FluidMessageBanner and a predefined instance of ContainerBannerProvider, OR
 *  2. FluidMessageBanner + ContainerBannerProvider
 */
export const ContainerMessageBanner = (props: ContainerMessageBannerProps) => {
  const { container, messageBannerActionHandlers, localization, logger, styles } = props;

  // Since this logger comes directly from the host app, wrap it with the appropriate namespace
  const namespacedLogger = logger && new NamespaceLoggerAdapter('ErrorUX', logger);

  const [errorInfo, setErrorInfo] = React.useState<BannerErrorInfo | undefined>();
  const [readonlyStateMessage, setReadonlyStateMessage] = React.useState<MessageBannerStrings | undefined>();
  const [disconnectedStateMessage, setDisconnectedStateMessage] = React.useState<MessageBannerStrings | undefined>();

  // This is only needed for additional strings like Reload action button text
  // Ideally, these type of string should come from host string table
  const stringTable = useStringTable(getStringTable, localization.locale);

  const handlePostLoadError = React.useCallback(async (error: ICriticalContainerError) => {
    const bannerErrorInfo = await getBannerMessagingForError(error, localization.locale);

    setErrorInfo(bannerErrorInfo);
  }, []);

  const handlePostLoadWarning = React.useCallback(async (warning: ContainerWarning) => {
    const bannerWarningInfo = await getBannerMessagingForError(warning, localization.locale);

    // We only show banners for container throttling
    // TODO:Task 4623211: Define messaging experience for other Container warnings
    if (bannerWarningInfo.errorType === ContainerErrorType.throttlingError) {
      setErrorInfo(bannerWarningInfo);
    }
  }, []);

  React.useEffect(() => {
    const containerClosedHandler = (error?: ICriticalContainerError) => {
      /**
       * Container may close for a variety of reasons, including the fact that a host may choose to close it
       * Only react to container close event with an error dialog if there's an error associated with the event
       */
      error && handlePostLoadError(error).catch(() => {});
    };

    const containerWarningHandler = (warning: ContainerWarning) => {
      handlePostLoadWarning(warning).catch(() => {});
    };

    //#region connection-state-handler
    /**
     * How long have we seen no activity from the user before we treat them as idle/inactive. Set to 1 hour.
     */
    const userInactiveAfterMs = 60 * 60 * 1000;

    const containerConnectionStateTracker = new ContainerConnectionStateTracker(
      container,
      (newState: 'connected' | 'disconnected') => {
        if (newState === 'connected') {
          setDisconnectedStateMessage(undefined);
          // Throttling error is a recoverable error state and the issue is resolved once we get the connected event
          // Hence clearing out any throttling error banners if the container gets a connected event
          // Switch to the on/off signals from runtime when that work is done
          // See github issue: https://github.com/microsoft/FluidFramework/issues/1768
          if (
            errorInfo &&
            (errorInfo.errorType === ContainerErrorType.throttlingError ||
              errorInfo.errorType === DriverErrorType.throttlingError)
          ) {
            setErrorInfo(undefined);
          }
        } else if (newState === 'disconnected' && !errorInfo) {
          // We only show disconnected banner if theres no active error shown
          getBannerMessagingForContainerState('Disconnected', localization.locale)
            .then((bannerMessaging) => {
              namespacedLogger &&
                sendTelemetryEvent(namespacedLogger, {
                  subCategory: GenericEventSubcategories.MessageBanner,
                  eventName: GenericEvent.DisconnectedBannerShown
                });
              setDisconnectedStateMessage(bannerMessaging);
            })
            .catch(() => {});
        }
      }
    );
    const onUserActivityStateChange = (isActive: boolean) => {
      // If the user becomes idle, we setAutoReconnect(false) so that we do not auto-reconnect the container on disconnection. This is done to save COGS on the server while the user is idle.
      // If the user becomes active, we set autoReconnect to true and try to reconnect container if it is disconnected.
      // TODO: Move reconnect logic lower in the stack based on generic browser idle properties
      if (!container.closed) {
        container.setAutoReconnect(isActive);
        if (isActive && !container.connected) {
          // Since we were not trying to establish a connection while the user was inactive, let's reset the connection state tracker to
          // give the app a chance to try an establish a connection.
          setDisconnectedStateMessage(undefined);
          if (containerConnectionStateTracker) {
            containerConnectionStateTracker.reset();
          }
        }
      }
    };

    const userActivityTracker = new UserActivityTracker(userInactiveAfterMs, onUserActivityStateChange);
    // #endregion connection-state-handler

    const containerReadonlyHandler = (readonly: boolean) => {
      if (readonly) {
        const state = isMobile() ? 'ReadonlyMobile' : 'Readonly';
        getBannerMessagingForContainerState(state, localization.locale)
          .then((bannerMessaging) => {
            setReadonlyStateMessage(bannerMessaging);
          })
          .catch(() => {});
      } else {
        setReadonlyStateMessage(undefined);
      }
    };

    container.on('closed', containerClosedHandler);
    container.on('warning', containerWarningHandler);
    container.on('readonly', containerReadonlyHandler);

    return () => {
      container.removeListener('closed', containerClosedHandler);
      container.removeListener('warning', containerWarningHandler);
      container.removeListener('readonly', containerReadonlyHandler);
      containerConnectionStateTracker.dispose();
      userActivityTracker.dispose();
    };
  }, [container]);

  const reloadAction = () => {
    errorInfo && messageBannerActionHandlers('Reload', errorInfo.errorType);
  };

  const upgradeAction = () => {
    errorInfo && messageBannerActionHandlers('Upgrade', errorInfo.errorType);
  };

  const viewFileLocationAction = () => {
    errorInfo && messageBannerActionHandlers('ViewFileLocation', errorInfo.errorType);
  };

  const reloadButton = (
    <MessageBarButton
      onClick={reloadAction}
      styles={{
        root: {
          width: 'auto'
        }
      }}
      ariaLabel={stringTable?.reloadButtonText}
      key={'reload'}
    >
      {stringTable?.reloadButtonText}
    </MessageBarButton>
  );

  const errorMessageBarStyles = applySelectiveBackgroundColor(MessageBarType.error, styles);
  const warningMessageBarStyles = applySelectiveBackgroundColor(MessageBarType.warning, styles);

  const selectErrorBanner = (errorInfo: BannerErrorInfo): JSX.Element => {
    switch (errorInfo.errorType) {
      case OWHErrorType.ContainerUpgradeRequired:
        return (
          <MessageBanner
            messageBarType={MessageBarType.warning}
            bannerStrings={errorInfo.bannerStrings}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXError')}
            actions={[
              <MessageBarButton
                onClick={upgradeAction}
                styles={{
                  root: {
                    width: 'auto'
                  }
                }}
                ariaLabel={stringTable?.upgradeButtonText}
                key={'upgrade'}
              >
                {stringTable?.upgradeButtonText}
              </MessageBarButton>
            ]}
            styles={warningMessageBarStyles}
          />
        );

      case DriverErrorType.fileNotFoundOrAccessDeniedError:
      case DriverErrorType.authorizationError:
        return (
          <MessageBanner
            messageBarType={MessageBarType.error}
            bannerStrings={errorInfo.bannerStrings}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXErrorBadge')}
            styles={errorMessageBarStyles}
          />
        );

      case OdspErrorType.epochVersionMismatch:
        return (
          <MessageBanner
            messageBarType={MessageBarType.warning}
            bannerStrings={errorInfo.bannerStrings}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXErrorBadge')}
            actions={[reloadButton]}
            styles={warningMessageBarStyles}
          />
        );

      case OdspErrorType.outOfStorageError:
        return (
          <MessageBanner
            messageBarType={MessageBarType.error}
            bannerStrings={errorInfo.bannerStrings}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXErrorBadge')}
            actions={[
              <MessageBarButton
                onClick={viewFileLocationAction}
                styles={{
                  root: {
                    width: 'auto'
                  }
                }}
                ariaLabel={stringTable?.viewFileLocationButtonText}
                key={'viewFileLocation'}
              >
                {stringTable?.viewFileLocationButtonText}
              </MessageBarButton>
            ]}
            styles={errorMessageBarStyles}
          />
        );

      case DriverErrorType.throttlingError:
      case ContainerErrorType.throttlingError:
      case DriverErrorType.genericError:
      case ContainerErrorType.genericError:
      default:
        return (
          <MessageBanner
            messageBarType={MessageBarType.error}
            bannerStrings={errorInfo.bannerStrings}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXErrorBadge')}
            actions={[reloadButton]}
            styles={errorMessageBarStyles}
          />
        );
    }
  };

  return errorInfo || readonlyStateMessage || disconnectedStateMessage ? (
    <Stack>
      {errorInfo && <Stack.Item key="error-banner">{selectErrorBanner(errorInfo)}</Stack.Item>}
      {readonlyStateMessage && (
        <Stack.Item key="readonly-banner">
          <MessageBanner
            messageBarType={MessageBarType.warning}
            bannerStrings={readonlyStateMessage}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXInfo')}
            styles={warningMessageBarStyles}
          />
        </Stack.Item>
      )}
      {disconnectedStateMessage && !errorInfo && (
        <Stack.Item key="disconnected-banner">
          <MessageBanner
            messageBarType={MessageBarType.warning}
            bannerStrings={disconnectedStateMessage}
            isMultiline={true}
            isAssertive={true}
            icon={useFFXHostUxIcon('FFXInfo')}
            styles={warningMessageBarStyles}
          />
        </Stack.Item>
      )}
    </Stack>
  ) : null;
};

function applySelectiveBackgroundColor(
  messageBarType: MessageBarType,
  styles?: ContainerMessageBarStyles
): IStyleFunctionOrObject<MessageBannerStyleProps, MessageBannerStyles> | undefined {
  const styleObject = typeof styles === 'function' ? styles({ theme: undefined }) : styles;

  if (!(styleObject && styleObject.background)) {
    return styleObject;
  }

  const { root, text, titleText, messageText, actionStyles, iconStyles, background } = styleObject;

  return {
    root: concatStyleSets(root, { background: background[messageBarType] }) as IStyle,
    text,
    titleText,
    messageText,
    actionStyles,
    iconStyles
  };
}

type ContainerMessageBarStyles = IStyleFunctionOrObject<
  MessageBannerStyleProps,
  MessageBannerStyles & {
    // Prevent direct customization of background as that would apply the same color to all message bar types!
    root?: Omit<IStyle, 'background'>;
    background?: Partial<
      {
        [name in MessageBarType]: string;
      }
    >;
  }
>;
